import { useCallback, useEffect, useState } from 'react';
import { Box, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel, SelectChangeEvent } from '@material-ui/core';
import { GetFormAnswers, DeleteFormAnswer } from '@bforms-web/common';
import { useLoading } from '../../../../../contexts/loading.context';
import { useAuth } from '../../../../../contexts/auth.context';
import FormTable from '../FormTable';
import { useStyles } from './styles';
import { toast } from 'react-toastify';
import React from 'react';
import SearchIcon from '@material-ui/icons/Search';

export type FormAnswersListProps = {
  getFormAnswers: GetFormAnswers;
  deleteFormAnswer: DeleteFormAnswer;
};

interface FormAnswers {
  id: string;
  acceptedTerms: boolean;
  formName: string;
  formFiles: Array<{ id: string, name: string, url: string }>;
  patientName: string;
  accreditedName: string;
  createdAt: string;
  termType?: 'FILE_UPLOADED' | 'SIGNED_ON_APP';
}

type FormAnswersListState = {
  formAnswers: FormAnswers[];
  total: number;
};

type SortOrder = 'patientName-asc' | 'patientName-desc' | 'createdAt-asc' | 'createdAt-desc';

type FilterState = {
  page: number;
  rows: number;
  timeout: number;
  search: string;
  sortOrder: SortOrder;
};

const defaultState: FormAnswersListState = {
  formAnswers: [],
  total: 0,
};

const defaultFilterState: FilterState = {
  page: 0,
  rows: 10,
  timeout: 0,
  search: '',
  sortOrder: 'patientName-asc',
};

export default function FormList({ getFormAnswers, deleteFormAnswer }: FormAnswersListProps): JSX.Element {
  const classes = useStyles();
  const [state, setState] = useState<FormAnswersListState>(defaultState);
  const [filterState, setFilterState] = useState<FilterState>(defaultFilterState);
  const [searchInput, setSearchInput] = useState('');
  const { showLoading, hideLoading } = useLoading();
  const { isAdmin } = useAuth();
  const FormTableMemo = React.memo(FormTable);

  const getServerOrderParams = useCallback((sortOrder: string) => {
    const [field, direction] = sortOrder.split('-') as [string, 'asc' | 'desc'];
    
    const fieldMap: Record<string, string> = {
      'patientName': 'patientName',
      'createdAt': 'createdAt',
      'formName': 'formName',
      'accreditedName': 'accreditedName'
    };
    
    return {
      orderBy: fieldMap[field] || 'patientName',
      orderDirection: direction.toUpperCase() as 'ASC' | 'DESC'
    };
  }, []);

  const filterFormAnswers = useCallback((data: FormAnswers[], search: string): FormAnswers[] => {
    if (!search) return data;
    const searchLower = search.toLowerCase();
    return data.filter(form => 
      (form.patientName?.toLowerCase().includes(searchLower) || '') ||
      (form.formName?.toLowerCase().includes(searchLower) || '') ||
      (form.accreditedName?.toLowerCase().includes(searchLower) || '')
    );
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setFilterState(prev => ({
      ...prev,
      page: Math.max(0, newPage),
    }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRows = parseInt(event.target.value, 10);
    setFilterState(prev => ({
      ...prev,
      rows: isNaN(newRows) ? 10 : Math.max(1, newRows),
      page: 0, 
    }));
  };

  const handleDelete = async (item: any) => {
    try {
      showLoading();
      await deleteFormAnswer.execute(item.id);
      toast.success('Termo de consentimento excluído com sucesso!');
      
      const currentPage = Math.max(0, Number(filterState.page) || 0);
      const rowsPerPage = Math.max(1, Number(filterState.rows) || 10);
      const skip = currentPage * rowsPerPage;
      
      const { orderBy, orderDirection } = getServerOrderParams(filterState.sortOrder);

      const response = await getFormAnswers.execute({
        programId: 'NoProgram',
        filter: filterState.search || 'NoFilter',
        skip,
        take: rowsPerPage,
        orderBy,
        orderDirection
      });

      let responseData = [];
      let responseTotal = 0;

      if (Array.isArray(response)) {
        responseData = response;
        responseTotal = response.length;
      } else if (response && typeof response === 'object') {
        responseData = response.data || [];
        responseTotal = response.total || 0;
      }

      const forms = responseData.map((formAnswer: any) => ({
        id: formAnswer.id,
        acceptedTerms: formAnswer.acceptedTerms,
        formName: formAnswer.form?.name || 'Sem nome',
        formFiles: formAnswer.formFiles || [],
        patientName: formAnswer.patient?.name || formAnswer.programPatient?.patient?.name || 'Paciente não encontrado',
        accreditedName: formAnswer.accredited?.corporateReason || formAnswer.programAccredited?.accredited?.corporateReason || 'Clínica não encontrada',
        createdAt: formAnswer.createdAt,
        termType: formAnswer.termType,
      }));

      setState({
        formAnswers: forms,
        total: responseTotal,
      });
    } catch (err) {
      console.error('Erro ao excluir termo de consentimento:', err);
      toast.error('Ocorreu um erro ao excluir o termo de consentimento.');
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    async function runAsync() {
      try {
        showLoading();
        const currentPage = Math.max(0, Number(filterState.page) || 0);
        const rowsPerPage = Math.max(1, Number(filterState.rows) || 10);
        const skip = currentPage * rowsPerPage;
        
        const { orderBy, orderDirection } = getServerOrderParams(filterState.sortOrder);

        const response = await getFormAnswers.execute({
          programId: 'NoProgram',
          filter: filterState.search || 'NoFilter',
          skip,
          take: rowsPerPage,
          orderBy,
          orderDirection
        });

        let responseData = [];
        let responseTotal = 0;

        if (Array.isArray(response)) {
          responseData = response;
          responseTotal = response.length;
        } else if (response && typeof response === 'object') {
          responseData = response.data || [];
          responseTotal = response.total || 0;
        }

        const forms = responseData.map((formAnswer: any) => ({
          id: formAnswer.id,
          acceptedTerms: formAnswer.acceptedTerms,
          formName: formAnswer.form?.name || 'Sem nome',
          formFiles: formAnswer.formFiles || [],
          patientName: formAnswer.patient?.name || formAnswer.programPatient?.patient?.name || 'Paciente não encontrado',
          accreditedName: formAnswer.accredited?.corporateReason || formAnswer.programAccredited?.accredited?.corporateReason || 'Clínica não encontrada',
          createdAt: formAnswer.createdAt,
          termType: formAnswer.termType,
        }));

        setState({
          formAnswers: forms,
          total: responseTotal,
        });
      } catch (err) {
        console.error('Erro ao carregar formulários:', err);
        toast.error('Ocorreu um erro ao carregar os formulários.');
      } finally {
        hideLoading();
      }
    }

    runAsync();
  }, [filterState, getFormAnswers, showLoading, hideLoading, getServerOrderParams]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    
    setFilterState(prev => ({
      ...prev,
      search: value,
      page: 0,
      timeout: 500,
    }));
  }, []);

  const handleSortChange = useCallback((event: SelectChangeEvent<SortOrder>) => {
    setFilterState(prev => ({
      ...prev,
      sortOrder: event.target.value as SortOrder,
      page: 0,
    }));
  }, []);

  return (
    <Box className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          variant="outlined"
          placeholder="Buscar por paciente, formulário ou clínica..."
          value={searchInput}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          style={{ width: 400 }}
        />
        <FormControl variant="outlined" style={{ minWidth: 200 }}>
          <InputLabel id="sort-order-label">Ordenar por</InputLabel>
          <Select
            labelId="sort-order-label"
            value={filterState.sortOrder}
            onChange={handleSortChange}
            label="Ordenar por"
          >
            <MenuItem value="patientName-asc">Paciente (A-Z)</MenuItem>
            <MenuItem value="patientName-desc">Paciente (Z-A)</MenuItem>
            <MenuItem value="createdAt-desc">Data (mais recente)</MenuItem>
            <MenuItem value="createdAt-asc">Data (mais antigo)</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <FormTableMemo
        data={state.formAnswers}
        total={state.total}
        page={filterState.page}
        rowsPerPage={filterState.rows}
        onChangePage={handleChangePage}
        onChangeRows={handleChangeRowsPerPage}
        onRemove={handleDelete}
        canRemove={isAdmin}
      />
    </Box>
  );
}